import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

// Helper seguro para localStorage (pode falhar em iframes de terceiros)
function safeSetItem(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* iframe context */ }
}
function safeGetItem(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeRemoveItem(key: string): void {
  try { localStorage.removeItem(key); } catch { /* iframe context */ }
}

interface User {
  id: number;
  nome: string;
  email: string;
  is_admin?: boolean;
  municipios?: any[];
  perfis?: any[];
  permissoes?: string[];
}

interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
}

interface AuthContextType {
  user: User | null;
  municipioAtivo: Municipio | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  ssoLogin: (cpf: string, token: string) => Promise<void>;
  logout: () => void;
  setMunicipioAtivo: (municipio: Municipio | null) => void;
  isAuthenticated: boolean;
  isInitialized: boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [municipioAtivo, setMunicipioAtivo] = useState<Municipio | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Carregar token e usuário do localStorage ao iniciar (persistência da sessão)
    const storedToken = safeGetItem('token');
    const storedUser = safeGetItem('user');
    const storedMunicipio = safeGetItem('municipio_ativo');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        if (storedMunicipio) {
          setMunicipioAtivo(JSON.parse(storedMunicipio));
        }
      } catch {
        safeRemoveItem('token');
        safeRemoveItem('user');
        safeRemoveItem('municipio_ativo');
      }
    }
    setIsInitialized(true);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);

    const token = response.access_token;
    const user = response.user;
    const municipio = response.municipio_ativo ?? null;

    setToken(token);
    setUser(user);
    setMunicipioAtivo(municipio);

    safeSetItem('token', token);
    safeSetItem('user', JSON.stringify(user));
    safeSetItem('municipio_ativo', JSON.stringify(municipio));
  };

  const ssoLogin = async (cpf: string, tokenSSO: string) => {
    const response = await authService.ssoLogin(cpf, tokenSSO);
    
    setToken(response.access_token);
    setUser(response.user);
    setMunicipioAtivo(response.municipio_ativo);

    safeSetItem('token', response.access_token);
    safeSetItem('user', JSON.stringify(response.user));
    safeSetItem('municipio_ativo', JSON.stringify(response.municipio_ativo));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setMunicipioAtivo(null);
    safeRemoveItem('token');
    safeRemoveItem('user');
    safeRemoveItem('municipio_ativo');
  };

  const changeMunicipioAtivo = (municipio: Municipio | null) => {
    setMunicipioAtivo(municipio);
    if (municipio) {
      safeSetItem('municipio_ativo', JSON.stringify(municipio));
    } else {
      safeRemoveItem('municipio_ativo');
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.permissoes) return false;
    return user.permissoes.includes(permission) || user.permissoes.includes('SYSTEM_ADMIN');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        municipioAtivo,
        token,
        login,
        ssoLogin,
        logout,
        setMunicipioAtivo: changeMunicipioAtivo,
        isAuthenticated: !!token,
        isInitialized,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
